import { z } from "zod";

/**
 * The persisted quote-document payload: the stored, self-contained HTML string
 * itself.
 *
 * The document is authored content (sections, headings, notes) written as plain
 * HTML with Tailwind classes and a small vocabulary of `<fp-*>` tags. Live quote
 * data (prices, quantities, descriptions) is never copied in - the fp-tags bind
 * fresh quote data at render time, so pricing stays in sync with the quote.
 *
 * This schema is the validation/sanitization seam every write path runs the raw
 * payload through before persisting it.
 */
export const QuoteDocumentSchema = z.string();

/** The persisted quote-document payload: the stored HTML string. */
export type QuoteDocument = z.infer<typeof QuoteDocumentSchema>;

/**
 * The document editor's read payload: the stored HTML plus its
 * optimistic-concurrency version, returned by the per-quote document GET.
 */
export const QuoteDocumentReadSchema = z.object({
  document: QuoteDocumentSchema,
  version: z.number().int(),
  baseTemplateId: z.string().nullable(),
});

/** The stored document HTML plus its optimistic-concurrency version. */
export type QuoteDocumentRead = z.infer<typeof QuoteDocumentReadSchema>;

/**
 * The document editor's write payload: the edited HTML plus the version the
 * edit was based on, checked by the server's optimistic-concurrency guard. The
 * document's `baseTemplateId` is set once at seed time and never rewritten.
 */
export const UpdateQuoteDocumentParamsSchema = z.object({
  document: QuoteDocumentSchema,
  expectedVersion: z.number().int(),
});

/** The edited document HTML plus the version the edit was based on. */
export type UpdateQuoteDocumentParams = z.infer<
  typeof UpdateQuoteDocumentParamsSchema
>;

/**
 * Machine-readable discriminators the document-write route stamps on its 409
 * responses, so the client can tell a recoverable edit conflict apart from a
 * permanent finalized-quote lock. A stale-version conflict offers reload-or-keep
 * recovery; a finalized lock is terminal and switches the editor to read-only.
 */
export const QUOTE_DOCUMENT_CONFLICT_CODE = {
  /** Optimistic-concurrency conflict: the stored version moved on. */
  CONFLICT: "conflict",
  /** The quote is finalized and its document can no longer be edited. */
  FINALIZED: "finalized",
} as const;

/** One document-write 409 discriminator code. */
export type QuoteDocumentConflictCode =
  (typeof QUOTE_DOCUMENT_CONFLICT_CODE)[keyof typeof QUOTE_DOCUMENT_CONFLICT_CODE];

/**
 * The document-write route's 409 error body: the human-readable message plus a
 * stable `code` distinguishing an edit conflict from a finalized-quote lock.
 */
export const QuoteDocumentConflictBodySchema = z.object({
  error: z.string(),
  code: z.enum([
    QUOTE_DOCUMENT_CONFLICT_CODE.CONFLICT,
    QUOTE_DOCUMENT_CONFLICT_CODE.FINALIZED,
  ]),
});

/** The document-write 409 error body carrying its discriminator code. */
export type QuoteDocumentConflictBody = z.infer<
  typeof QuoteDocumentConflictBodySchema
>;

/** Maximum length of a quote-document template name. */
export const QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH = 200;

/** A quote-document template as returned by the template routes. */
export const QuoteDocumentTemplateSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
});

/** The identity fields of a quote-document template. */
export type QuoteDocumentTemplateSummary = z.infer<
  typeof QuoteDocumentTemplateSummarySchema
>;

/** The template list reply: the org's non-archived templates. */
export const ListQuoteDocumentTemplatesReplySchema = z.object({
  templates: z.array(QuoteDocumentTemplateSummarySchema),
});

/** The org's non-archived quote-document templates. */
export type ListQuoteDocumentTemplatesReply = z.infer<
  typeof ListQuoteDocumentTemplatesReplySchema
>;

/**
 * The create-template payload: the new template's name and, to capture a quote's
 * stored document, the quote. Alternatively accepts raw template HTML.
 */
export const CreateQuoteDocumentTemplateParamsSchema = z
  .object({
    quoteId: z.string().min(1).optional(),
    name: z.string().trim().min(1).max(QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH),
    document: QuoteDocumentSchema.optional(),
  })
  .refine(
    (value) => value.quoteId === undefined || value.document === undefined,
    {
      message: "Provide either a quote or template HTML, not both",
    },
  );

/** The quote and name for a save-as-template call. */
export type CreateQuoteDocumentTemplateParams = z.infer<
  typeof CreateQuoteDocumentTemplateParamsSchema
>;

/** The save-as-template reply: the created template. */
export const CreateQuoteDocumentTemplateReplySchema = z.object({
  template: QuoteDocumentTemplateSummarySchema,
});

/** The created template. */
export type CreateQuoteDocumentTemplateReply = z.infer<
  typeof CreateQuoteDocumentTemplateReplySchema
>;

/**
 * A template with its stored document, returned by the single-template GET that
 * seeds the template editor. The list reply stays identity-only, since the
 * pickers never need the HTML.
 */
export const QuoteDocumentTemplateDetailSchema =
  QuoteDocumentTemplateSummarySchema.extend({
    document: QuoteDocumentSchema,
    /** Signed, org-scoped asset URLs keyed by the document's `asset-id` values. */
    assetUrls: z.record(z.string(), z.string()),
  });

/** A template's identity fields plus its stored document. */
export type QuoteDocumentTemplateDetail = z.infer<
  typeof QuoteDocumentTemplateDetailSchema
>;

/** The single-template GET reply: the template and its document. */
export const GetQuoteDocumentTemplateReplySchema = z.object({
  template: QuoteDocumentTemplateDetailSchema,
});

/** The requested template and its document. */
export type GetQuoteDocumentTemplateReply = z.infer<
  typeof GetQuoteDocumentTemplateReplySchema
>;

/**
 * The template-edit payload: a replacement document, a replacement name, or
 * both. Editing a template rewrites it in place; quotes copy a template's
 * document when they are created, so existing quotes keep the document they
 * were seeded with.
 */
export const UpdateQuoteDocumentTemplateParamsSchema = z.object({
  document: QuoteDocumentSchema.optional(),
  name: z
    .string()
    .trim()
    .min(1)
    .max(QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH)
    .optional(),
});

/** The replacement document and/or name for a template edit. */
export type UpdateQuoteDocumentTemplateParams = z.infer<
  typeof UpdateQuoteDocumentTemplateParamsSchema
>;

/** The template-edit reply: the updated template. */
export const UpdateQuoteDocumentTemplateReplySchema = z.object({
  template: QuoteDocumentTemplateSummarySchema,
});

/** The updated template. */
export type UpdateQuoteDocumentTemplateReply = z.infer<
  typeof UpdateQuoteDocumentTemplateReplySchema
>;

/**
 * The `<fp-*>` custom-element vocabulary a quote document may use. These tags
 * bind live quote data at render time; every other tag in a document is plain
 * authored HTML. This is the single source of truth the server-side sanitizer
 * enforces and the renderer recognizes.
 */
export const FP_TAGS = [
  "fp-field",
  "fp-markdown",
  "fp-image",
  "fp-page-number",
  "fp-page-break",
  "fp-lines",
  "fp-breaks",
  "fp-line-field",
  "fp-text",
  "fp-columns",
  "fp-table",
  "fp-column",
  "fp-row",
  "fp-cell",
  "fp-divider",
  "fp-section-heading",
  "fp-section-notes",
  "fp-header",
  "fp-footer",
  "fp-page-count",
  "fp-page-frame",
] as const;

/** A member of the `<fp-*>` document tag vocabulary. */
export type FpTag = (typeof FP_TAGS)[number];

/** Semantic `fp-image` asset id resolving the active organization's logo. */
export const FP_ORG_LOGO_ASSET_ID = "org-logo";

/** Source prefix binding a custom field by name, at quote or line scope. */
export const FP_CUSTOM_SOURCE_PREFIX = "custom:";

/** Source prefix binding the quote's total weight for a named weight unit. */
export const FP_TOTAL_WEIGHT_SOURCE_PREFIX = "total-weight:";

/** Quote-scope sources resolvable by `fp-field` and `fp-markdown`. */
export const FP_FIELD_SOURCES = [
  "org-name",
  "org-address",
  "org-website",
  "org-phone",
  "org-fax",
  "org-headquarters",
  "org-contact-line",
  "location",
  "location-name",
  "location-address",
  "letterhead-name",
  "letterhead-address",
  "sales-rep-name",
  "sales-rep-email",
  "estimator-names",
  "customer-name",
  "customer-company-name",
  "customer-company-phone",
  "customer-company-email",
  "customer-company-address",
  "quote-people-names",
  "quote-people-emails",
  "ship-to-address",
  "opportunity-name",
  "quote-name",
  "quote-number",
  "effective-date",
  "expiration-date",
  "quote-footer",
  "subtotal",
  "subtotal-with-discounts",
  "total",
  "signer-name",
  "signed-date",
  "cover-letter",
  "payment-terms",
  "delivery-terms",
  "terms-of-service",
] as const;

/** One quote-scope `fp-field` / `fp-markdown` source key. */
export type FpFieldSource = (typeof FP_FIELD_SOURCES)[number];

/** Line-scope sources resolvable by `fp-line-field` inside a repeater. */
export const FP_LINE_FIELD_SOURCES = [
  "number",
  "description",
  "item-name",
  "item-description",
  "item-image",
  "quantity",
  "unit-price",
  "amount",
  "weight",
  "weight-unit",
  "weight-unit-symbol",
  "length",
  "width",
  "height",
  "dimension-unit",
  "dimension-unit-symbol",
  "unit",
  "item-revision",
] as const;

/** One line-scope `fp-line-field` source key. */
export type FpLineFieldSource = (typeof FP_LINE_FIELD_SOURCES)[number];

/**
 * Attribute making any element conditional: the element renders only when its
 * value holds. The value is either a named condition from
 * `FP_SHOW_WHEN_CONDITIONS` or a field source, which holds while that field
 * resolves non-empty. The two vocabularies are disjoint, so one attribute
 * covers both.
 */
export const FP_SHOW_WHEN_ATTRIBUTE = "data-fp-show-when";

/** Marks the element whose cells are an item row's editable columns. */
export const FP_COLUMNS_ATTRIBUTE = "data-fp-columns";

/** Marks the row of authored headings titling those columns. */
export const FP_COLUMN_HEADER_ATTRIBUTE = "data-fp-column-header";

/**
 * Carries a section note's original markdown alongside its rendered HTML. The
 * rendered prose is what customers see; the markdown is what a reader hands
 * back when it restates the sections, so a heading rename never flattens the
 * note's emphasis or list structure.
 */
export const FP_NOTE_MARKDOWN_ATTRIBUTE = "data-fp-note-markdown";

/** Named conditions resolvable by the `data-fp-show-when` attribute. */
export const FP_SHOW_WHEN_CONDITIONS = [
  "has-multiple-breaks",
  "has-items",
  "discounts-change-subtotal",
  "has-terms",
  "signed",
  "show-totals",
  "show-total-weight",
  "expired",
] as const;

/** One `data-fp-show-when` named-condition key. */
export type FpShowWhenCondition = (typeof FP_SHOW_WHEN_CONDITIONS)[number];

/**
 * Prefix inverting a `data-fp-show-when` value, so the complement of every
 * condition is writable: `!has-items` holds exactly when `has-items` does not.
 */
export const FP_SHOW_WHEN_NEGATION_PREFIX = "!";

/**
 * Operator joining `data-fp-show-when` conditions, so one attribute can express
 * a gate that was two nested elements before: `has-items && !signed` holds only
 * when both of its conjuncts do. It is the only operator; there is no `||`, no
 * parentheses, and no precedence to reason about. It is read as an operator
 * only where the text after it names a condition, so an `&&` inside a
 * `custom:` field name stays part of the name (see `parseShowWhenValue`).
 */
export const FP_SHOW_WHEN_CONJUNCTION_OPERATOR = "&&";

/** One conjunct of a `data-fp-show-when` value: a bare condition and its negation flag. */
export interface FpShowWhenConjunct {
  negated: boolean;
  condition: string;
}

/**
 * Splits a `data-fp-show-when` value into the conjuncts it ANDs together, each
 * carrying its own negation flag.
 * @param value - The raw `data-fp-show-when` attribute value
 * @returns One entry per conjunct, in written order, prefixes stripped
 */
export function parseShowWhenValue(value: string): FpShowWhenConjunct[] {
  return splitShowWhenConjuncts(value).map(parseShowWhenConjunct);
}

/**
 * Splits a value at its `&&` operators, keeping an `&&` that the author meant
 * as literal text inside an open-ended source's parameter.
 *
 * `custom:<Field Name>` accepts any non-empty name, so a field really called
 * "R&D && QA" was storable long before `&&` became an operator and must keep
 * parsing as one conjunct. Splitting on every `&&` would break it; letting
 * `custom:` swallow the whole remainder instead would break `custom:has-po &&
 * signed`, which is the far commoner thing to write. The tie-break is what
 * follows the operator: a conjunct is only started when the text after `&&`
 * names something the grammar can resolve, so `signed` opens a new conjunct
 * and `QA` is read as more of the field name.
 * @param value - The raw `data-fp-show-when` attribute value
 * @returns The value's conjuncts as raw, still-unparsed slices
 */
function splitShowWhenConjuncts(value: string): string[] {
  return value
    .split(FP_SHOW_WHEN_CONJUNCTION_OPERATOR)
    .reduce<string[]>((slices, part) => {
      const previous = slices[slices.length - 1];
      if (previous === undefined || !absorbsOperator(previous, part)) {
        return [...slices, part];
      }
      const joined = `${previous}${FP_SHOW_WHEN_CONJUNCTION_OPERATOR}${part}`;
      return [...slices.slice(0, -1), joined];
    }, []);
}

/**
 * Whether the `&&` between two slices is literal text rather than an operator.
 * @param previous - The slice before the operator
 * @param next - The slice after the operator
 * @returns True when the operator belongs to the previous slice's parameter
 */
function absorbsOperator(previous: string, next: string): boolean {
  return (
    isOpenEndedSource(parseShowWhenConjunct(previous).condition) &&
    !isResolvableCondition(parseShowWhenConjunct(next).condition)
  );
}

/**
 * Whether a condition is a parameterized source whose parameter is free text,
 * and so may itself contain an `&&`.
 * @param condition - The condition with any negation prefix already stripped
 * @returns True when the condition carries a free-text parameter
 */
function isOpenEndedSource(condition: string): boolean {
  return (
    condition.startsWith(FP_CUSTOM_SOURCE_PREFIX) ||
    condition.startsWith(FP_TOTAL_WEIGHT_SOURCE_PREFIX)
  );
}

/**
 * Whether a condition names something the show-when grammar can resolve, in any
 * scope. Deliberately scope-blind: a value must split the same way for the
 * renderer and the validator, so a segment that only resolves inside a repeater
 * still opens a conjunct and is rejected there by the scope rules rather than
 * silently folded into the field name before it.
 * @param condition - The condition with any negation prefix already stripped
 * @returns True when the condition names a condition or a field source
 */
function isResolvableCondition(condition: string): boolean {
  return (
    RESOLVABLE_CONDITION_NAMES.has(condition) || isOpenEndedSource(condition)
  );
}

/** Every bare name the show-when grammar resolves, across both scopes. */
const RESOLVABLE_CONDITION_NAMES: ReadonlySet<string> = new Set<string>([
  ...FP_SHOW_WHEN_CONDITIONS,
  ...FP_FIELD_SOURCES,
  ...FP_LINE_FIELD_SOURCES,
]);

/**
 * Splits one conjunct into its negation flag and the condition the flag applies to.
 * @param part - One `&&`-delimited slice of the attribute value
 * @returns The negation flag and the condition with the prefix stripped
 */
function parseShowWhenConjunct(part: string): FpShowWhenConjunct {
  const trimmed = part.trim();
  if (!trimmed.startsWith(FP_SHOW_WHEN_NEGATION_PREFIX)) {
    return { negated: false, condition: trimmed };
  }
  return {
    negated: true,
    condition: trimmed.slice(FP_SHOW_WHEN_NEGATION_PREFIX.length).trim(),
  };
}

/** Line-type filters accepted by the `fp-lines` `filter` attribute. */
export const FP_LINES_FILTERS = [
  "items",
  "discounts",
  "notes",
  "sections",
  "all",
] as const;

/** One `fp-lines` filter value. */
export type FpLinesFilter = (typeof FP_LINES_FILTERS)[number];

/**
 * Counter shapes accepted by the `fp-page-number` `format` attribute: `n`
 * prints the page on its own, `n-of-total` pairs it with the count as "3/6",
 * and `page-n-of-total` spells the phrase out as "Page 3 of 6". The paired
 * forms fold the count in, so they stand alone where the bare `n` needs a row
 * of labels and a separate `<fp-page-count>` beside it.
 */
export const FP_PAGE_NUMBER_FORMATS = [
  "n",
  "n-of-total",
  "page-n-of-total",
] as const;

/** One `fp-page-number` format value. */
export type FpPageNumberFormat = (typeof FP_PAGE_NUMBER_FORMATS)[number];

/**
 * Repetition scopes accepted by the `fp-table` `scope` attribute: `sections`
 * groups the quote's lines under their section heading and notes, `flat` lists
 * them ungrouped. A table declaring neither is a static table instead.
 */
export const FP_TABLE_SCOPES = ["sections", "flat"] as const;

/** One `fp-table` scope value. */
export type FpTableScope = (typeof FP_TABLE_SCOPES)[number];

/** Internal admin import request: copy a document and its assets to an organization. */
export const ImportQuoteDocumentTemplateParamsSchema = z.object({
  organizationId: z.string().regex(/^org_[a-zA-Z0-9]+$/),
  name: z.string().trim().min(1).max(QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH),
  document: QuoteDocumentSchema.max(512 * 1024).refine(
    (value) => value.trim().length > 0,
    "Template HTML is required",
  ),
});

/** Parameters for an internal template import. */
export type ImportQuoteDocumentTemplateParams = z.infer<
  typeof ImportQuoteDocumentTemplateParamsSchema
>;

/** Import result with the destination template and source-to-destination image mapping. */
export const ImportQuoteDocumentTemplateReplySchema = z.object({
  template: QuoteDocumentTemplateSummarySchema,
  assetIdMap: z.record(z.string(), z.string()),
});

/** A newly imported template and its copied asset IDs. */
export type ImportQuoteDocumentTemplateReply = z.infer<
  typeof ImportQuoteDocumentTemplateReplySchema
>;
